import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DoneIcon from '@material-ui/icons/Done';
import { DialogModal, Form, Input, useHandleOpen } from '@engloba-tech/englobity';
import { NAME_SPACE } from 'i18n';
import { isSkeletonLoading, SelectInput, enumHelper, useRequestLoading, TOBACCO_RING_POSITION } from 'shared';
import { FormFileUpload } from 'pages/shared';
import { settings, KEY_SETTINGS } from 'settings';
import { useTobaccoProductForm } from './useTobaccoProductForm';
import { useFormTobaccoProductStyles } from './formTobacco.styles';
import { ProductImage } from '../ProductImage';

export function FormTobaccoProduct({ formRef, onSubmit, product, errors, onChangeInput }) {
  const { t } = useTranslation(NAME_SPACE.TOBACCO_PRODUCTS);
  const classes = useFormTobaccoProductStyles();
  const { callbackRequest } = useRequestLoading();
  const { productInputs, handleChange, handleSelectFile, handleDeleteFile, errorInfoFiles } = useTobaccoProductForm(
    product,
    onChangeInput
  );

  const maxFileSize = settings.getSetting(KEY_SETTINGS.MAX_IMAGE_SIZE);

  const tobaccoRingPositions = useMemo(() => {
    return enumHelper.enumToCombo(TOBACCO_RING_POSITION, t, 'ringPositions', true);
  }, [t]);

  function handleSubmit() {
    onSubmit(productInputs);
  }

  const $formFileUploadRef = useRef(null);
  const {
    isOpen: isOpenAddFile,
    handleClose: handleCloseAddFile,
    handleOpen: handleOpenAddFile
  } = useHandleOpen(false);

  async function handleMediaFileUploadSubmit(selectedFiles) {
    if (selectedFiles) {
      await callbackRequest(async () => {
        await handleSelectFile(selectedFiles[0]);
        handleCloseAddFile();
      });
    }
  }

  function handleModalAcceptUpload() {
    $formFileUploadRef.current.submit();
  }

  return (
    <>
      <Form
        className={classes.formProduct}
        errors={errors}
        elementRef={formRef}
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        <Grid container spacing={3}>
          <Grid sm={12} md={6} item>
            <Input
              isLoading={isSkeletonLoading(productInputs.name)}
              required
              name={t('properties.name')}
              label={t('properties.name')}
              variant="outlined"
              value={productInputs.name || ''}
              onChange={handleChange('name')}
              inputProps={{ 'aria-label': t('properties.name') }}
              validators={['required', 'maxStringLength:255']}
              errorMessages={[t('validations.required'), t('common:validations.maxStringLength', { length: 255 })]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              isLoading={isSkeletonLoading(productInputs.cigarAmount)}
              required
              name={t('properties.cigarAmount')}
              label={t('properties.cigarAmount')}
              variant="outlined"
              type="number"
              value={productInputs.cigarAmount ?? ''}
              onChange={handleChange('cigarAmount')}
              inputProps={{ 'aria-label': t('properties.cigarAmount') }}
              validators={['required', 'minNumber:1', 'matchRegexp:^[0-9]+$']}
              errorMessages={[
                t('validations.required'),
                t('validations.minNumber', { minimum: 1 }),
                t('validations.noDecimal')
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              isLoading={isSkeletonLoading(productInputs.litterAmount)}
              required
              name={t('properties.litterAmount')}
              label={t('properties.litterAmount')}
              variant="outlined"
              type="number"
              value={productInputs.litterAmount ?? ''}
              onChange={handleChange('litterAmount')}
              inputProps={{ 'aria-label': t('properties.litterAmount') }}
              validators={['required', 'minNumber:1', 'matchRegexp:^[0-9]+$']}
              errorMessages={[
                t('validations.required'),
                t('validations.minNumber', { minimum: 1 }),
                t('validations.noDecimal')
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              isLoading={isSkeletonLoading(productInputs.length)}
              required
              name={t('properties.length')}
              label={t('properties.length')}
              variant="outlined"
              type="number"
              value={productInputs.length ?? ''}
              onChange={handleChange('length')}
              inputProps={{ 'aria-label': t('properties.length') }}
              validators={['required', 'minNumber:0']}
              errorMessages={[t('validations.required'), t('validations.minNumber', { minimum: 0 })]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              isLoading={isSkeletonLoading(productInputs.width)}
              required
              name={t('properties.width')}
              label={t('properties.width')}
              variant="outlined"
              type="number"
              value={productInputs.width ?? ''}
              onChange={handleChange('width')}
              inputProps={{ 'aria-label': t('properties.width') }}
              validators={['required', 'minNumber:0']}
              errorMessages={[t('validations.required'), t('validations.minNumber', { minimum: 0 })]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              isLoading={isSkeletonLoading(productInputs.height)}
              required
              name={t('properties.height')}
              label={t('properties.height')}
              variant="outlined"
              type="number"
              value={productInputs.height ?? ''}
              onChange={handleChange('height')}
              inputProps={{ 'aria-label': t('properties.height') }}
              validators={['required', 'minNumber:0']}
              errorMessages={[t('validations.required'), t('validations.minNumber', { minimum: 0 })]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectInput
              name={t('properties.tobaccoRingPosition')}
              label={t('properties.tobaccoRingPosition')}
              elements={tobaccoRingPositions}
              required
              value={productInputs.tobaccoRingPosition}
              onChange={handleChange('tobaccoRingPosition')}
              validators={['required']}
              errorMessages={[t('validations.required')]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              isLoading={isSkeletonLoading(productInputs.masterCaseCaseAmount)}
              required
              name={t('properties.masterCaseCaseAmount')}
              label={t('properties.masterCaseCaseAmount')}
              variant="outlined"
              type="number"
              value={productInputs.masterCaseCaseAmount ?? ''}
              onChange={handleChange('masterCaseCaseAmount')}
              inputProps={{ 'aria-label': t('properties.masterCaseCaseAmount') }}
              validators={['required', 'minNumber:1', 'matchRegexp:^[0-9]+$']}
              errorMessages={[
                t('validations.required'),
                t('validations.minNumber', { minimum: 1 }),
                t('validations.noDecimal')
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              isLoading={isSkeletonLoading(productInputs.nfcReadInstructions)}
              multiline
              rows={5}
              className={classes.textarea}
              name={t('properties.nfcReadInstructions')}
              label={t('properties.nfcReadInstructions')}
              variant="outlined"
              value={productInputs.nfcReadInstructions || ''}
              onChange={handleChange('nfcReadInstructions')}
              inputProps={{ 'aria-label': t('properties.nfcReadInstructions') }}
              validators={['maxStringLength:2000']}
              errorMessages={[t('common:validations.maxStringLength', { length: 2000 })]}
              onKeyPress={e => {
                if (e.which === 13) {
                  e.stopPropagation();
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <ProductImage
              src={productInputs.imageUrl}
              readToken={productInputs.readToken}
              title={t('properties.imageUrl')}
              canBeDeleted={productInputs.imageUrl}
              onDelete={() => handleDeleteFile()}
              onClick={handleOpenAddFile}
              showTitleBottom
            />
          </Grid>
        </Grid>
      </Form>

      <DialogModal
        title={t('common:upload.dialogTitle')}
        description=""
        fullWidth
        isOpen={isOpenAddFile}
        onClose={handleCloseAddFile}
        buttons={[
          {
            children: <CloseIcon />,
            text: t('common:actions.cancel'),
            type: 'secondary',
            onClick: handleCloseAddFile
          },
          {
            children: <DoneIcon />,
            text: t('common:actions.add'),
            type: 'primary',
            onClick: handleModalAcceptUpload
          }
        ]}
      >
        <FormFileUpload
          formRef={$formFileUploadRef}
          onSubmit={handleMediaFileUploadSubmit}
          errors={errorInfoFiles}
          accept="image/*"
          maxFiles={1}
          maxFileSize={maxFileSize}
        />
      </DialogModal>
    </>
  );
}

FormTobaccoProduct.propTypes = {
  formRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  onSubmit: PropTypes.func,
  errors: PropTypes.shape({
    message: PropTypes.string,
    detail: PropTypes.string
  }),
  product: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    cigarAmount: PropTypes.number,
    litterAmount: PropTypes.number,
    length: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    tobaccoRingPosition: PropTypes.number,
    masterCaseCaseAmount: PropTypes.number
  }),
  onChangeInput: PropTypes.func
};
